import React, { useEffect, useState } from "react";
import Modal from "Components/Common/GenericModal";
import Button from "Components/Common/Button";
import Pills from "Components/Common/Pills";
import "./styles.scss";
import {
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import AssignIssueModal from "Components/Modals/AssignIssueModal";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  AssignIssueToProfessional,
  GetAllStaff,
  UpdateReportStatus,
} from "endpoints/reports/index.action";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

interface ViewReportDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  issue?: any;
  refetchRequests: () => void;
}

const ViewReportDetailsModal: React.FC<ViewReportDetailsModalProps> = ({
  isOpen,
  onClose,
  issue,
  //   refetchRequests,
}) => {
  const { t } = useTranslation();
  const [assignOpen, setAssignOpen] = useState(false);
  const [staff, setStaff] = useState<any[]>([]);
  const [localIssue, setLocalIssue] = useState<any | null>(issue ?? null);
  const [selectedStaff, setSelectedStaff] = useState<any | null>(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [checked, setChecked] = useState(
    issue?.status === "Closed" || issue?.status === "Completed" || false
  );
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);

  useEffect(() => {
    setChecked(
      issue?.status === "Closed" || issue?.status === "Completed" || false
    );
  }, [issue]);

  React.useEffect(() => {
    setLocalIssue(issue ?? null);
    setSelectedStaff(issue?.staff || null);
  }, [issue]);

  // Keyboard navigation for image lightbox
  useEffect(() => {
    if (!imageModalOpen || !issue?.attachments || issue.attachments.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setImageModalOpen(false);
      } else if (e.key === 'ArrowLeft') {
        setSelectedImageIndex((prev) =>
          prev > 0 ? prev - 1 : issue.attachments.length - 1
        );
      } else if (e.key === 'ArrowRight') {
        setSelectedImageIndex((prev) =>
          prev < issue.attachments.length - 1 ? prev + 1 : 0
        );
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [imageModalOpen, issue?.attachments]);

  const title = (
    <div className="view-report-header">
      <div className="view-report-icon-wrapper">
        <img
          src="/assets/icons/WarningIcon.png"
          alt="issue"
          className="view-report-icon"
        />
      </div>

      <div className="view-report-header-info">
        <div className="view-report-type">{issue?.type}</div>
        <div className="view-report-title">{issue?.title}</div>
      </div>

      <div className="view-report-status">
        <Pills
          label={issue?.status ?? "Unknown"}
          variant={
            issue?.status === "Completed" || issue?.status === "Closed"
              ? "success"
              : issue?.status === "Revoked" || issue?.status === "Used"
              ? "danger"
              : "warning"
          }
          size="sm"
        />
      </div>
    </div>
  );

  useQuery({
    queryKey: ["fetchStaffProfiles"],
    queryFn: async () => {
      const response = await GetAllStaff();
      if (response && response.data) {
        setStaff(response.data);
      }
      return response;
    },
    enabled: isOpen,
  });

  const assignMutation = useMutation({
    mutationKey: ["assign-issue"],
    mutationFn: async (professional: any) => {
      if (!issue?.id) throw new Error(t("reports.reportIdRequired"));
      const response = await AssignIssueToProfessional(
        issue?.backendId,
        professional.id
      );
      if (response?.statusCode !== 200) {
        throw new Error(
          response?.message || t("reports.failedToAssignIssue")
        );
      }
      return response;
    },
    onError: (error: any) => {
      toast.error(
        error?.message || t("reports.failedToAssignIssue")
      );
    },
  });

  const markAsClosedMutation = useMutation({
    mutationKey: ["mark-issue-closed"],
    mutationFn: async () => {
      const response = await UpdateReportStatus(issue?.backendId, "closed");
      if (response?.statusCode !== 200) {
        throw new Error(
          response?.message ||
            t("reports.failedToMarkAsClosed")
        );
      }
      return response;
    },
    onSuccess: () => {
      toast.success(t("reports.issueMarkedAsClosed"));
      setLocalIssue((prev: any) => {
        if (!prev) return prev;
        return { ...prev, status: "closed" };
      });
    },
    onError: (error: any) => {
      toast.error(
        error?.message || t("reports.failedToMarkAsClosed")
      );
    },
  });

  const handleAssign = (professional: any) => {
    assignMutation.mutate(professional, {
      onSuccess: () => {
        toast.success(t("reports.issueAssignedSuccessfully"));
        // update local issue staff so UI reflects change immediately
        setLocalIssue((prev: any) => {
          if (!prev) return prev;
          return { ...prev, staff: professional };
        });
        setSelectedStaff(professional);
        setAssignOpen(false);
      },
    });
  };

  const handleAssignClick = () => {
    if (selectedStaff) {
      handleAssign(selectedStaff);
    } else {
      toast.error(t("reports.pleaseSelectStaff"));
    }
  };

  const footer = (
    <div className="view-report-footer">
      <div className="view-report-footer-assign">
        <Dropdown
          isOpen={dropdownOpen}
          toggle={() => setDropdownOpen(!dropdownOpen)}
          className="view-report-staff-dropdown"
        >
          <DropdownToggle caret className="view-report-dropdown-toggle">
            {selectedStaff
              ? selectedStaff.name ||
                `${selectedStaff.firstName || ""} ${selectedStaff.lastName || ""}`.trim() ||
                t("common.viewDetails")
              : t("reports.assignTo")}
          </DropdownToggle>
          <DropdownMenu className="view-report-dropdown-menu">
            {staff.length > 0 ? (
              staff.map((staffMember: any) => {
                const staffName =
                  staffMember.name ||
                  `${staffMember.firstName || ""} ${staffMember.lastName || ""}`.trim();
                return (
                  <DropdownItem
                    key={staffMember.id}
                    onClick={() => {
                      setSelectedStaff(staffMember);
                      setDropdownOpen(false);
                    }}
                    active={selectedStaff?.id === staffMember.id}
                    className="view-report-dropdown-item-wrapper"
                  >
                    <div className="view-report-dropdown-item">
                      <span className="view-report-dropdown-name">{staffName}</span>
                      {staffMember.category && (
                        <span className="view-report-dropdown-category">
                          {staffMember.category}
                        </span>
                      )}
                    </div>
                  </DropdownItem>
                );
              })
            ) : (
              <DropdownItem disabled>{t("reports.noStaffAvailable")}</DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>
        <Button
          variant="primary"
          size="md"
          onClick={handleAssignClick}
          loading={assignMutation.isPending}
          disabled={assignMutation.isPending || !selectedStaff}
          className="view-report-assign-button"
        >
          {t("reports.assign")}
        </Button>
      </div>
      <div className="view-report-footer-close">
        <Button
          variant={checked ? "primary" : "secondary"}
          size="md"
          onClick={() => {
            if (!checked) {
              markAsClosedMutation.mutate();
            }
          }}
          loading={markAsClosedMutation.isPending}
          disabled={
            markAsClosedMutation.isPending ||
            localIssue?.status?.toLowerCase() === "closed" ||
            localIssue?.status?.toLowerCase() === "completed" ||
            checked
          }
          className="view-report-close-button"
        >
          {checked ? t("common.closed") : t("reports.markAsClosed")}
        </Button>
      </div>
    </div>
  );


  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      description={null}
      size="md"
      closable={false}
      className="view-issue-modal"
    >
      {title}
      <div className="view-report-body">
        <div className="view-report-section">
          <div className="view-report-section-title">{t("reports.description")}</div>
          <div className="view-report-description">{localIssue?.description}</div>
        </div>

        <div className="view-report-details-pills">
          <div className="view-report-pill">
            <i className="ri-calendar-line" aria-hidden />
            <span>{issue?.occurence}</span>
          </div>
          <div className="view-report-pill view-report-pill-urgency">
            <i className="ri-alert-line" aria-hidden />
            <span>{t("reports.urgency")}: {issue?.severity}</span>
          </div>
        </div>

        <div className="view-report-section">
          <div className="view-report-section-title">{t("reports.attachments")}</div>
          <div className="view-report-attachments">
            {issue?.attachments && issue?.attachments.length > 0 ? (
              issue?.attachments.map((attachment: any, index: number) => (
                <div 
                  key={index} 
                  className="view-report-attachment-item"
                  onClick={() => {
                    setSelectedImageIndex(index);
                    setImageModalOpen(true);
                  }}
                  style={{ cursor: 'pointer' }}
                  title={t("reports.clickToViewFullSize")}
                >
                  <img
                    src={attachment?.url}
                    alt={`attachment-${index}`}
                    className="view-report-attachment-img"
                  />
                </div>
              ))
            ) : (
              <div className="view-report-no-attachments">{t("reports.noAttachmentsAvailable")}</div>
            )}
          </div>
        </div>

        {issue?.staff?.id && (
          <div className="view-report-section">
            <div className="view-report-section-title">{t("reports.assignedTo")}</div>
            <div className="view-report-assigned-item">
              <div className="view-report-assigned-avatar">
                {localIssue?.staff?.profileImage ? (
                  <img
                    src={localIssue?.staff?.profileImage}
                    alt={localIssue?.staff?.name}
                    className="view-report-assigned-avatar-img"
                  />
                ) : (
                  <span className="view-report-assigned-avatar-initial">
                    {localIssue?.staff?.name?.charAt(0)}
                  </span>
                )}
              </div>
              <div className="view-report-assigned-info">
                <div className="view-report-assigned-name">{localIssue?.staff?.name}</div>
                {localIssue?.staff?.category && (
                  <div className="view-report-assigned-category">
                    {localIssue?.staff?.category}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
      {issue?.status !== "Revoked" && footer}
      <AssignIssueModal
        isOpen={assignOpen}
        onClose={() => setAssignOpen(false)}
        professionals={staff}
        onAssign={(p) => {
          handleAssign(p);
        }}
      />
      
      {/* Image Lightbox Modal */}
      {imageModalOpen && issue?.attachments && issue.attachments.length > 0 && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.9)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
          }}
          onClick={() => setImageModalOpen(false)}
        >
          <div
            style={{
              position: 'relative',
              maxWidth: '90vw',
              maxHeight: '90vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={issue.attachments[selectedImageIndex]?.url}
              alt={`attachment-${selectedImageIndex}`}
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                objectFit: 'contain',
                borderRadius: '8px',
              }}
            />
            
            {/* Navigation Arrows */}
            {issue.attachments.length > 1 && (
              <>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex((prev) =>
                      prev > 0 ? prev - 1 : issue.attachments.length - 1
                    );
                  }}
                  style={{
                    position: 'absolute',
                    left: '20px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '50px',
                    height: '50px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: '#333',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  }}
                >
                  ‹
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedImageIndex((prev) =>
                      prev < issue.attachments.length - 1 ? prev + 1 : 0
                    );
                  }}
                  style={{
                    position: 'absolute',
                    right: '20px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'rgba(255, 255, 255, 0.9)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '50px',
                    height: '50px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '24px',
                    color: '#333',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  }}
                >
                  ›
                </button>
              </>
            )}
            
            {/* Close Button */}
            <button
              onClick={() => setImageModalOpen(false)}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(255, 255, 255, 0.9)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '24px',
                color: '#333',
                boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              }}
            >
              ×
            </button>
            
            {/* Image Counter */}
            {issue.attachments.length > 1 && (
              <div
                style={{
                  position: 'absolute',
                  bottom: '20px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  background: 'rgba(0, 0, 0, 0.7)',
                  color: 'white',
                  padding: '8px 16px',
                  borderRadius: '20px',
                  fontSize: '14px',
                }}
              >
                {selectedImageIndex + 1} / {issue.attachments.length}
              </div>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default ViewReportDetailsModal;
